import { ref, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import axios from 'axios';
import type { Router } from 'vue-router';

import { WarehouseFeatureEnum, isFeatureEnabledForWarehouse, type IWarehouse } from '@nova/core';

import cometApiService, { type CheckinPayload } from '@/services/cometApiService';
import useApiErrors from '@/components/composables/useApiErrors';
import { useCheckinFlow } from '@/composables/useCheckinFlow';

export interface InlineSubmitError {
  icon: string;
  title: string;
}

export function useCheckinSubmission(warehouse: Ref<Partial<IWarehouse>>) {
  const { t, locale } = useI18n();
  const checkinFlow = useCheckinFlow();
  const { submitPlanned, submitUnplanned } = cometApiService();
  const { makeErrorScreenParams } = useApiErrors();

  const isSubmitting = ref(false);
  const inlineError = ref<InlineSubmitError | null>(null);

  function buildMessagingFailedQuery() {
    return { messagingError: checkinFlow.useWhatsApp.value ? 'whatsapp' : 'sms' };
  }

  function buildPayload(): CheckinPayload {
    const isSmartGateEnabled = isFeatureEnabledForWarehouse(
      warehouse.value,
      WarehouseFeatureEnum.SmartGate
    );
    const notes = (checkinFlow.notes.value || '').replace(/\n/gu, '<br />');

    return {
      latitude: checkinFlow.latitude.value,
      longitude: checkinFlow.longitude.value,
      appointmentId: checkinFlow.appointmentId.value || null,
      assetDetails: {
        assetVisitType: checkinFlow.assetVisitType.value,
        companyId: checkinFlow.companyId.value,
        companyHint: checkinFlow.companyName.value,
        driverNotes: notes,
        driverAppointmentIdentifier: checkinFlow.identifier.value,
        driverLanguageTag: locale.value,
        phone: checkinFlow.phoneNumber.value,
        useWhatsApp: checkinFlow.useWhatsApp.value,
        ...(isSmartGateEnabled
          ? {
              licensePlate: checkinFlow.licensePlate.value,
              dotNumber: checkinFlow.dotNumber.value
            }
          : {})
      },
      customFormsData: checkinFlow.customFormsData.value,
      warehouseId: warehouse.value.id,
      assetContainerDetails: checkinFlow.assetContainerData.value,
      notes,
      inquiryId: checkinFlow.inquiryId.value
    };
  }

  async function submit(router: Router): Promise<void> {
    inlineError.value = null;

    const warehouseId = warehouse.value.id;

    if (!warehouseId) {
      inlineError.value = { icon: 'mdi-alert-circle-outline', title: t('errors.api.genericTitle') };
      return;
    }

    const requiresGeofencing = warehouse.value.settings?.gateManagementRequiresGeofencing;
    const hasLocation = checkinFlow.latitude.value !== null && checkinFlow.longitude.value !== null;

    if (requiresGeofencing && !hasLocation) {
      inlineError.value = {
        icon: 'mdi-map-marker-alert-outline',
        title: t('errors.api.locationValidationTitle')
      };
      return;
    }

    isSubmitting.value = true;

    try {
      const payload = buildPayload();
      const data = checkinFlow.appointmentId.value
        ? await submitPlanned<{ initialMessageFailed?: boolean }>(warehouseId, payload)
        : await submitUnplanned<{ initialMessageFailed?: boolean }>(warehouseId, payload);

      router.push({
        name: 'completed',
        query: data?.initialMessageFailed ? buildMessagingFailedQuery() : {}
      });
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const errorParams = makeErrorScreenParams(error);

        if (errorParams.showErrorInline === '1') {
          inlineError.value = { icon: errorParams.icon, title: errorParams.title };
        } else {
          router.push({
            name: 'appointment-error',
            query: { ...errorParams, warehouseId: warehouse.value.id }
          });
        }
      } else {
        inlineError.value = {
          icon: 'mdi-alert-circle-outline',
          title: t('errors.api.genericTitle')
        };
      }
    } finally {
      isSubmitting.value = false;
    }
  }

  return { isSubmitting, inlineError, submit };
}
